import { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaClock, FaGift, FaMapMarkerAlt, FaStore, FaTimes, FaTrophy } from 'react-icons/fa';
import { CouponDoc } from '~/services/db/coupons/CouponsAPI';
import { OfferDoc } from '~/services/db/offers/OffersAPI';
import { CouponCloudFunctionAPI } from '~/services/coupons/CouponCloudFunctionAPI';
import styles from './CouponRewardScreen.module.css';

type Props = {
    coupon: CouponDoc;
    offer?: OfferDoc;
    clientID: string;
    token: string | null;
};

function getRemainingMs(target: Date) {
    const now = new Date().getTime();
    return Math.max(0, target.getTime() - now);
}

export function CouponRewardScreen({ coupon, offer, clientID, token }: Props) {
    const { t } = useTranslation();
    const validUntil = coupon.validUntil?.toDate?.() ?? new Date();
    const [remainingMs, setRemainingMs] = useState<number>(getRemainingMs(validUntil));
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    const [showExpiredScreen, setShowExpiredScreen] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [showEmailToast, setShowEmailToast] = useState(false);
    // Track initial status to only show expired screen if coupon was already validated on first render
    const initialStatusRef = useRef(coupon.status);

    // Check if email was sent from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const emailSent = urlParams.get('emailSent') === 'true';

    // Show toast when email was sent and auto-dismiss after 5 seconds
    useEffect(() => {
        if (emailSent) {
            setShowEmailToast(true);
            const timer = setTimeout(() => {
                setShowEmailToast(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [emailSent]);

    const isExpired = remainingMs <= 0;
    // Only consider it "already used" if it was validated on initial render, not if we just validated it
    const wasAlreadyUsedInitially = initialStatusRef.current === 'validated';

    const scopeLabels: Record<string, string> = {
        daily: t('coupon.records.daily'),
        weekly: t('coupon.records.weekly'),
        monthly: t('coupon.records.monthly'),
        yearly: t('coupon.records.yearly'),
        'all-time': t('coupon.records.allTime'),
        participation: t('coupon.records.participation'),
    };

    useEffect(() => {
        // Show expired screen only if coupon was already validated on initial render
        // Don't show it if we just validated it ourselves (showSuccessScreen takes precedence)
        if (wasAlreadyUsedInitially && !showExpiredScreen && !showSuccessScreen) {
            setShowExpiredScreen(true);
        }
    }, [wasAlreadyUsedInitially, showExpiredScreen, showSuccessScreen]);

    useEffect(() => {
        const id = setInterval(() => {
            const remaining = getRemainingMs(validUntil);
            setRemainingMs(remaining);
            // When timer reaches 0, show expired screen
            if (remaining <= 0 && !showExpiredScreen) {
                setShowExpiredScreen(true);
            }
        }, 1000);
        return () => clearInterval(id);
    }, [validUntil, showExpiredScreen]);

    const remainingMinutes = Math.ceil(remainingMs / 60000);

    const durationMinutes = useMemo(() => {
        const issuedAt = coupon.issuedAt?.toDate?.();
        if (!issuedAt) return 60; // sensible default
        const total = Math.max(1, Math.round((validUntil.getTime() - issuedAt.getTime()) / 60000));
        return total;
    }, [coupon.issuedAt, validUntil]);

    const progress = 1 - Math.min(1, remainingMs / (durationMinutes * 60000));

    const offerTitle = coupon.offerName || offer?.title || t('coupon.reward.yourReward');
    const offerDesc = t('coupon.reward.validForMinutes', { minutes: remainingMinutes });
    const locationText = offer?.shopPlace || t('coupon.reward.location');
    const shopName = offer?.shopName || '';
    const scopeLabel = scopeLabels[coupon?.leaderboardScope || 'daily'] || scopeLabels.daily;

    const handleValidateClick = () => {
        // Check if expired or already used (on initial render) before showing confirmation
        if (isExpired || wasAlreadyUsedInitially) {
            setShowExpiredScreen(true);
            return;
        }
        setShowConfirmationModal(true);
    };

    const handleConfirmValidation = async () => {
        if (!token) {
            alert(t('coupon.reward.validationError'));
            return;
        }

        setShowConfirmationModal(false);
        setIsValidating(true);

        try {
            const result = await CouponCloudFunctionAPI.validateCoupon({
                token,
                couponId: coupon.id,
                clientID,
            });

            if (result.success) {
                setShowSuccessScreen(true);
            } else {
                alert(result.message || t('coupon.reward.validationError'));
            }
        } catch (err) {
            console.error('Failed to validate coupon:', err);
            alert(t('coupon.reward.validationError'));
        } finally {
            setIsValidating(false);
        }
    };

    const handleCancelValidation = () => {
        setShowConfirmationModal(false);
    };

    // Expired or already used screen
    if (showExpiredScreen) {
        const message = wasAlreadyUsedInitially ? t('coupon.reward.alreadyUsed') : t('coupon.reward.expired');
        const detail = wasAlreadyUsedInitially
            ? t('coupon.reward.alreadyUsedMessage')
            : t('coupon.reward.expiredMessage');

        return (
            <div className={styles.rewardContent}>
                <div className={styles.expiredScreen}>
                    <div className={styles.expiredTitle}>{offerTitle}</div>
                    <div className={styles.expiredIconContainer}>
                        <div className={styles.expiredIcon}>
                            <FaTimes />
                        </div>
                    </div>
                    <div className={styles.expiredMessage}>{message}</div>
                    <div className={styles.expiredDetail}>{detail}</div>
                </div>
            </div>
        );
    }

    // Success screen
    if (showSuccessScreen) {
        return (
            <div className={styles.rewardContent}>
                <div className={styles.successScreen}>
                    <div className={styles.successTitle}>{offerTitle}</div>
                    <div className={styles.successIconContainer}>
                        <div className={styles.successIcon}>
                            <FaCheck />
                        </div>
                    </div>
                    <div className={styles.successMessage}>{t('coupon.reward.validated')}</div>
                    <div className={styles.successDetail}>{t('coupon.reward.validatedMessage')}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.rewardContent}>
            {showEmailToast && (
                <div className={styles.emailToast}>
                    <FaCheck />
                    <span>{t('coupon.reward.sentByEmail')}</span>
                </div>
            )}

            <div className={styles.sectionHeader}>
                <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>{t('coupon.reward.yourReward')}</div>
                    <div className={styles.recordBanner}>
                        <FaTrophy className={styles.recordIcon} />
                        <span>{scopeLabel}</span>
                    </div>
                </div>
                <button className={styles.giftButton} type="button">
                    <FaGift />
                </button>
            </div>

            <div className={styles.rewardCard}>
                <div className={styles.rewardTitle}>{offerTitle}</div>
                <div className={styles.nonCumulativeText}>{t('coupon.reward.nonCumulative')}</div>
                <div className={styles.rewardDesc}>{offerDesc}</div>
                <div className={styles.rewardLocationRow}>
                    <div className={styles.rewardLocation}>
                        <FaMapMarkerAlt />
                        <span>{locationText}</span>
                    </div>
                    {shopName && (
                        <div className={styles.rewardLocation}>
                            <FaStore />
                            <span>{shopName}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.timerPanel}>
                <div className={styles.timerHeader}>
                    <FaClock />
                    <span>{t('coupon.reward.timeRemaining')}</span>
                </div>
                <div className={styles.timerValue}>{remainingMinutes}</div>
                <div className={styles.timerUnit}>
                    {remainingMinutes === 1 ? t('coupon.reward.minute') : t('coupon.reward.minutes')}
                </div>
                <div className={styles.progressTrack}>
                    <div className={styles.progressBar} style={{ width: `${progress * 100}%` }} />
                </div>
            </div>

            <div className={styles.primaryBanner}>
                <span>{t('coupon.reward.validateRewardInfo')}</span>
            </div>

            <button
                className={styles.bigCta}
                onClick={handleValidateClick}
                disabled={isValidating || isExpired || wasAlreadyUsedInitially}
            >
                <FaGift />
                <span>{t('coupon.reward.validateReward')}</span>
            </button>

            {showConfirmationModal && (
                <div className={styles.modalOverlay} onClick={handleCancelValidation}>
                    <div className={styles.confirmationModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalTitle}>{t('coupon.reward.confirmValidation')}</div>
                        <div className={styles.modalMessage}>
                            <p>
                                <strong>{t('coupon.reward.confirmMessage')}</strong>
                            </p>
                            <p>{t('coupon.reward.confirmWarning')}</p>
                        </div>
                        <div className={styles.modalActions}>
                            <button className={styles.modalCancelButton} onClick={handleCancelValidation}>
                                {t('coupon.reward.cancel')}
                            </button>
                            <button className={styles.modalConfirmButton} onClick={handleConfirmValidation}>
                                {t('coupon.reward.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CouponRewardScreen;
